/**
 * @file MeetingNotePicker.tsx
 * @brief Attach one of your notes to a meeting — the meeting's side of ADR-023.
 *
 * @details
 * The mirror of `NoteMeetingPicker`: same link, opposite direction. This one
 * lists the caller's NOTES and attaches them to a fixed meeting, and it lives
 * in the notes feature because the notes are what it knows about — the meeting
 * detail view only opens it.
 *
 * The role is still the server's decision, and the toast reports what it
 * decided rather than what the meeting's status suggested a moment ago.
 *
 * @dependencies
 * - components/ui/PickerSheet
 * - features/notes/api
 *
 * @references
 * - NoteMeetingPicker.tsx (the same operation from the note)
 * - docs/decisions/ADR-023-note-meeting-links.md
 *
 * @author DES
 * @date 21-08-2026
 */

import { useMemo, useState, type ReactElement } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { formatDateTime } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { ApiClientError } from "@/shared/api/client";
import { useAttachNoteToMeeting, useNotesList } from "./api";
import { notePoints } from "./schemas";

export function MeetingNotePicker({
  open,
  onOpenChange,
  anchor,
  meetingId,
  attachedNoteIds,
  askVisibility,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The button it opens under, as a menu (30-09-2026). */
  anchor?: ReactElement;
  meetingId: string;
  /** Already attached — shown ticked, and tapping one does nothing here.
   *  Removing is done on the row itself, where the note is named. */
  attachedNoteIds: string[];
  /** True when this meeting is someone ELSE's — the case ADR-024 introduced,
   *  where the choice is asked rather than assumed. On your own meeting the
   *  semantics are unchanged and the row carries a share control afterwards. */
  askVisibility: boolean;
}) {
  // Notes only: an agenda cannot be attached after the meeting (DES 12-10-2026).
  const notes = useNotesList({ page: 1, pageSize: 50, kind: "note" });
  const attach = useAttachNoteToMeeting();
  const [pending, setPending] = useState<PickerSheetItem | null>(null);

  const items: PickerSheetItem[] = useMemo(
    () =>
      (notes.data?.items ?? []).map((n) => {
        const points = notePoints(n);
        // Points say what the note contains, whichever kind it is: ADR-025
        // gave a recording points of its own, so the old typed-only branch
        // showed a transcript slice where its points belong. The slice remains
        // for a recording still being read, which has no points yet.
        const gist =
          points.length > 0 ? points.join(" · ") : (n.transcript_text ?? "").slice(0, 120);
        return {
          id: n.id,
          label: n.title?.trim() || "Untitled note",
          subtitle: `${formatDateTime(n.created_at)}${gist ? ` · ${gist}` : ""}`,
        };
      }),
    [notes.data],
  );

  const runAttach = (item: PickerSheetItem, shared: boolean) => {
    attach.mutate(
      { meetingId, noteId: item.id, shared },
      {
        onSuccess: ({ role }) =>
          toast({
            title: role === "prep" ? "Agenda attached" : "Note attached",
            description:
              role === "prep"
                ? "Its points will be checked against this meeting when it is processed."
                : shared
                  ? "Everyone in this meeting can read it. It is not part of the meeting's intelligence."
                  : "Stored with this meeting, visible only to you. You can share it from its row.",
          }),
        onError: (e) =>
          toast({
            variant: "destructive",
            title: "Could not attach",
            description: e instanceof ApiClientError ? e.message : "Please try again.",
          }),
      },
    );
  };

  return (
    <>
      <PickerSheet
        anchor={anchor}
        // Its button sits at the page's right edge: open leftwards, inside
        // the page (DES 01-10-2026: the menu touched the border).
        menuAlign="end"
        open={open}
        onOpenChange={onOpenChange}
        title="Attach a note"
        subtitle="Your own notes. Attach one to this meeting."
        searchPlaceholder="Search notes"
        items={items}
        multiSelect
        selectedIds={attachedNoteIds}
        loading={notes.isLoading}
        clearable={false}
        emptyText="You have no notes yet."
        onSelect={(item) => {
          if (attachedNoteIds.includes(item.id) || attach.isPending) {
            return;
          }
          if (askVisibility) {
            onOpenChange(false);
            setPending(item);
            return;
          }
          runAttach(item, false);
        }}
      />

      {/* Asked, not assumed: both intentions are real — filing a private note
        against a meeting you attended, and contributing a point to the room —
        and publishing by accident is the mistake that cannot be taken back. */}
      <AlertDialog open={!!pending} onOpenChange={(next) => !next && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Who should see this note?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pending?.label}” will be added to this meeting, which someone else recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pending) runAttach(pending, false);
                setPending(null);
              }}
            >
              Only me
            </AlertDialogAction>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pending) runAttach(pending, true);
                setPending(null);
              }}
            >
              Everyone in the meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default MeetingNotePicker;
